import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["submitButton", "termsCheckbox"];

  static values = {
    address: Object,
    customerId: Number,
    visitId: Number,
    entryMethod: String
  };

  connect() {
    this.isSubmitting = false;  // Prevent double-clicks
    this.updateSubmitButtonState();
  }

  handleTermsChange() {
    this.updateSubmitButtonState();
  }

  updateSubmitButtonState() {
    if (!this.hasTermsCheckboxTarget || !this.hasSubmitButtonTarget) {
      return;
    }

    const isChecked = this.termsCheckboxTarget.checked;
    const isDisabled = !isChecked || this.isSubmitting;

    this.submitButtonTarget.disabled = isDisabled;
    this.updateSubmitButtonText();
    this.updateSubmitButtonStyle(isDisabled);
  }

  updateSubmitButtonText() {
    if (!this.isSubmitting) {
      this.submitButtonTarget.textContent = "Securely Save Payment Method";
    }
  }

  updateSubmitButtonStyle(isDisabled) {
    const disabledClasses = ['tw-opacity-50', 'tw-cursor-not-allowed'];
    const enabledClasses = ['hover:tw-bg-teal-800'];

    if (isDisabled) {
      this.submitButtonTarget.classList.add(...disabledClasses);
      this.submitButtonTarget.classList.remove(...enabledClasses);
    } else {
      this.submitButtonTarget.classList.remove(...disabledClasses);
      this.submitButtonTarget.classList.add(...enabledClasses);
    }
  }

  notifyError(error) {
    if (typeof window.Honeybadger === "object") {
      const context = {
        customer_id: this.hasCustomerIdValue ? this.customerIdValue : null,
        visit_id: this.hasVisitIdValue ? this.visitIdValue : null,
        form_type: this.hasEntryMethodValue ? this.entryMethodValue : null
      };
      window.Honeybadger.notify(error, { context });
    } else {
      console.error("Honeybadger is not available. Error:", error);
    }
  }

  appendHiddenField(name, value) {
    const formTarget = this.getFormTarget();
    if (!formTarget) {
      console.error(`Form target not found for appending ${name}`);
      return;
    }

    // Remove any existing hidden input with the same name
    const existingInput = formTarget.querySelector(`input[name="${name}"]`);
    if (existingInput) {
      existingInput.remove();
    }

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = name;
    hiddenInput.value = value;
    formTarget.appendChild(hiddenInput);
  }

  // To be overridden by subclasses
  getFormTarget() {
    throw new Error("getFormTarget must be implemented by subclass");
  }

  /**
   * Safely returns the value of a Stimulus target if it exists, otherwise a default.
   * Example: getTargetValue("accountOwnerName", "Unknown")
   * @param {string} targetName - camelCase name without "Target" suffix
   * @param {*} defaultValue - value to return when target is absent
   * @returns {*}
   */
  getTargetValue(targetName, defaultValue = "") {
    const hasKey = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (this[hasKey]) {
      const targetAccessor = `${targetName}Target`;
      return this[targetAccessor].value;
    }
    return defaultValue;
  }

  /**
   * Enable/disable the submit button and optionally change its label.
   * @param {boolean} disabled
   * @param {string} label
   */
  toggleSubmitButton(disabled, label = "Securely Save Payment Method") {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = disabled;
    this.submitButtonTarget.textContent = label;
  }

  /**
   * Start submission process with race condition prevention and timeout.
   * @returns {boolean} true if submission started, false if already submitting
   */
  startSubmission() {
    if (this.isSubmitting) {
      return false;
    }
    this.isSubmitting = true;
    this.toggleSubmitButton(true, "Creating...");

    // 10-second timeout
    this.timeoutId = setTimeout(() => {
      this.displayError("This is taking too long. Please try again.");
      this.resetSubmission();
    }, 10000);

    return true;
  }

  resetSubmission() {
    clearTimeout(this.timeoutId);
    this.isSubmitting = false;
    // Update button state based on terms checkbox
    this.updateSubmitButtonState();
  }

  /**
   * Ensure an error container element exists inside the controller root and return it.
   * Creates one when missing.
   * @returns {HTMLElement}
   */
  ensureErrorContainer() {
    let container = this.element.querySelector('[data-error-container]');
    if (container) return container;

    container = document.createElement('div');
    container.setAttribute('data-error-container', '');
    container.className = 'tw-mt-2 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-text-sm tw-text-red-600';

    // Try to insert near primary form element when available
    const insertPoint = ("hasJustifiBankAccountFormTarget" in this && this.hasJustifiBankAccountFormTarget)
      ? this.justifiBankAccountFormTarget.parentElement
      : this.element.querySelector('.tw-max-w-2xl') || this.element;

    insertPoint.insertBefore(container, insertPoint.firstChild);
    return container;
  }

  displayError(message) {
    const container = this.ensureErrorContainer();
    container.textContent = message;
    container.style.display = 'block';
  }

  clearError() {
    const container = this.element.querySelector('[data-error-container]');
    if (container) container.style.display = 'none';
  }

  // Build address data with form field fallback to addressValue
  buildAddressData() {
    const addressValue = this.hasAddressValue ? this.addressValue : {};
    return {
      address_line1: this.getTargetValue('addressStreet1', addressValue.street_1 || ''),
      address_line2: this.getTargetValue('addressStreet2', addressValue.street_2 || ''),
      address_city: this.getTargetValue('addressCity', addressValue.city || ''),
      address_state: this.getTargetValue('addressState', addressValue.province_alpha2 || ''),
      address_postal_code: this.getTargetValue('addressPostalCode', addressValue.postal_code || ''),
      address_country: this.getTargetValue('addressCountry', addressValue.country_alpha2 || 'US')
    };
  }

  /**
   * Wrap a promise with a timeout, returning a Promise.race between the original and timeout.
   * @param {Promise} promise - The promise to wrap with a timeout
   * @param {number} timeoutMs - Timeout duration in milliseconds (default: 3000)
   * @param {string} timeoutMessage - Error message on timeout (default: 'Operation timeout')
   * @returns {Promise} A Promise.race between the original promise and timeout
   */
  withTimeout(promise, timeoutMs = 3000, timeoutMessage = 'Operation timeout') {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
    );
    return Promise.race([promise, timeoutPromise]);
  }
}
